import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../CSS/MonthlyNews.css';

interface MonthlyNews {
  monthlyNewsId: number;
  monthlyNewsTitle: string;
  monthlyNewsDescription: string;
  monthlyNewsURL: string;
  createdDate: string;
  lastUpdated: string;
}

const MonthlyNewsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isFromAdmin = searchParams.get('fromAdmin') === 'true';
  const [searchQuery, setSearchQuery] = useState('');
  const [monthlyNewsList, setMonthlyNewsList] = useState<MonthlyNews[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchMonthlyNews = async () => {
      try {
        setIsLoading(true);
        // Production API endpoint
        const response = await fetch('/api/admin/monthly-news');
        // const response = await fetch('http://localhost:3060/api/admin/monthly-news');
        const data = await response.json();

        if (data.success && data.data) {
          // Sort by createdDate descending (most recent first)
          const sortedNews = [...data.data].sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA; // Most recent first
          });
          setMonthlyNewsList(sortedNews);
        } else {
          setMonthlyNewsList([]);
        }
      } catch (error) {
        console.error('Error fetching monthly news:', error);
        setMonthlyNewsList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMonthlyNews();
  }, []);

  // Helper function to strip HTML tags from text for searching
  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Filter monthly news based on search query - search through all available fields
  const filteredMonthlyNews = searchQuery.trim() === ''
    ? monthlyNewsList
    : monthlyNewsList.filter(news => {
        const searchLower = searchQuery.toLowerCase();
        const descText = stripHtmlTags(news.monthlyNewsDescription || '').toLowerCase();
        
        return (
          news.monthlyNewsTitle.toLowerCase().includes(searchLower) ||
          descText.includes(searchLower) ||
          news.monthlyNewsURL.toLowerCase().includes(searchLower)
        );
      });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      alert('First Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      alert('Email is required');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Production API endpoint
      const response = await fetch('/api/admin/subscriber-information', {
        // const response = await fetch('http://localhost:3060/api/admin/subscriber-information', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's a duplicate email error
        if (response.status === 409 || data.error?.includes('already exists') || data.error?.includes('already in use')) {
          alert('Email is already in use. Please try again.');
          return;
        }
        // Other errors
        throw new Error(data.error || 'Failed to submit subscription');
      }

      // Success - show success message
      alert('Thank you for subscribing! Your account has been created.');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: ''
      });
    } catch (error: any) {
      console.error('Error submitting subscription:', error);
      // Only show generic error if it's not a duplicate email (already handled above)
      if (!error.message?.includes('already')) {
        alert('Error submitting subscription. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to convert database file path to API endpoint URL
  const getFileUrl = (filePath: string | null | undefined): string => {
    if (!filePath || !filePath.trim()) {
      return '';
    }
    // If path already starts with http, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    // Convert database path to API endpoint
    // Database path: Files/Monthly News/filename.pdf
    // API endpoint: /api/admin/monthly-news/file/Files/Monthly News/filename.pdf
    const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
    // Production API endpoint
    return `/api/admin/monthly-news/file/${cleanPath}`;
    // return `http://localhost:3060/api/admin/monthly-news/file/${cleanPath}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="monthly-news-page" style={isFromAdmin ? { marginTop: '10vh' } : {}}>
      {/* Dark Blue Banner */}
      <div className="monthly-news-banner">
        <div className="monthly-news-breadcrumb">
          <Link to="/">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/news-and-insights">News & Insights</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">E-Newsletters</span>
        </div>
        <h1 className="monthly-news-title">E-Newsletters</h1>
      </div>

      {/* White Content Area */}
      <div className="monthly-news-content">
        {/* Search Bar */}
        <div className="monthly-news-search-container">
          <div className="monthly-news-search-bar">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="monthly-news-search-input"
            />
            <button className="monthly-news-search-icon-button" aria-label="Search">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Subscribe Section */}
        <div className="monthly-news-subscribe-section">
          <h2 className="monthly-news-subscribe-title">Subscribe</h2>
          <p className="monthly-news-subscribe-description">
            Sign up with your email address to receive news and updates.
          </p>
          <form className="monthly-news-subscribe-form" onSubmit={handleSubmit}>
            <div className="monthly-news-form-row">
              <div className="monthly-news-form-field">
                <label htmlFor="firstName" className="monthly-news-form-label">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="monthly-news-form-input"
                  required
                />
              </div>
              <div className="monthly-news-form-field">
                <label htmlFor="lastName" className="monthly-news-form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="monthly-news-form-input"
                />
              </div>
              <div className="monthly-news-form-field">
                <label htmlFor="email" className="monthly-news-form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="monthly-news-form-input"
                  required
                />
              </div>
              <div className="monthly-news-form-submit-container">
                <button 
                  type="submit" 
                  className="monthly-news-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Monthly News List */}
        {isLoading ? (
          <div className="monthly-news-loading">Loading monthly news...</div>
        ) : filteredMonthlyNews.length === 0 ? (
          <div className="monthly-news-empty">
            {searchQuery.trim() !== '' 
              ? 'No monthly news found matching your search.'
              : 'There is no monthly news. Please check back again at a later date.'}
          </div>
        ) : (
          <div className="monthly-news-list">
            {filteredMonthlyNews.map((news) => (
              <div key={news.monthlyNewsId} className="monthly-news-card">
                <h3 className="monthly-news-card-title">
                  {news.monthlyNewsTitle}
                </h3>
                <p className="monthly-news-card-description">
                  {news.monthlyNewsDescription}
                </p>
                <a 
                  href={getFileUrl(news.monthlyNewsURL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="monthly-news-card-read-more"
                  onClick={(e) => {
                    // If it's a PDF file, ensure it opens in a new tab
                    if (news.monthlyNewsURL.toLowerCase().endsWith('.pdf') || 
                        news.monthlyNewsURL.toLowerCase().includes('.pdf')) {
                      e.preventDefault();
                      window.open(getFileUrl(news.monthlyNewsURL), '_blank');
                    }
                  }}
                >
                  Read More
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyNewsPage;

